import { DjiFlightDetail } from '../data-models/dji-flight-detail.model';
import { DjiFlightFilters } from '../request-models/dji-flight-filters.model';
import { DjiFlightRow } from '../data-models/dji-flight-row.model';
import { DjiLinkStatus } from '../data-models/dji-link-status.model';
import { DjiLinkView } from '../response-models/dji-link-view.model';
import { DjiRelinkOutcome } from '../response-models/dji-relink-outcome.model';
import { DjiStatusCounts } from '../data-models/dji-status-counts.model';

/** Where the admin list sends a filter that matches no organization. */
export const NO_ORGANIZATION = 'none';

export interface DjiStatusTab {
  key: 'all' | 'attention' | 'linked' | 'dismissed';
  label: string;
  /** Empty means every status. */
  statuses: DjiLinkStatus[];
}

/**
 * The list's tabs. "Needs attention" is everything a person may have to act
 * on: flights that matched nothing, are ambiguous, or could not be verified.
 */
export const DJI_STATUS_TABS: DjiStatusTab[] = [
  { key: 'all', label: 'All', statuses: [] },
  { key: 'attention', label: 'Needs attention', statuses: ['pending', 'ambiguous', 'unverified'] },
  { key: 'linked', label: 'Linked', statuses: ['linked', 'imported'] },
  { key: 'dismissed', label: 'Dismissed', statuses: ['dismissed', 'invalid'] },
];

/** The count beside a tab: the sum of the statuses it groups. */
export function tabCount(counts: DjiStatusCounts | null | undefined, tab: DjiStatusTab): number {
  if (!counts) return 0;
  return tab.statuses.length === 0 ? counts.total : tab.statuses.reduce((sum, status) => sum + (counts[status] ?? 0), 0);
}

/** The tab a set of statuses belongs to (the URL carries statuses, the page shows a tab). */
export function tabForStatuses(statuses: readonly string[] | undefined): DjiStatusTab {
  const key = [...(statuses ?? [])].sort().join(',');
  return DJI_STATUS_TABS.find((tab) => [...tab.statuses].sort().join(',') === key) ?? DJI_STATUS_TABS[0];
}

const STATUS_LABELS: Record<DjiLinkStatus, string> = {
  pending: 'Not linked',
  linked: 'Linked to a drone',
  imported: 'Linked to a spray record',
  unverified: 'Unverified',
  dismissed: 'Dismissed',
  invalid: 'Marked invalid',
  ambiguous: 'Ambiguous',
};

const STATUS_BADGES: Record<DjiLinkStatus, string> = {
  pending: 'bg-warning text-dark',
  linked: 'bg-info text-dark',
  imported: 'bg-success',
  unverified: 'bg-warning text-dark',
  dismissed: 'bg-secondary',
  invalid: 'bg-dark',
  ambiguous: 'bg-danger',
};

export function statusLabel(status: DjiLinkStatus): string {
  return STATUS_LABELS[status] ?? status;
}

export function statusBadgeClass(status: DjiLinkStatus): string {
  return STATUS_BADGES[status] ?? 'bg-secondary';
}

/** True for the statuses a person set on purpose; the Hub refuses everything but restore on them. */
export function isSetAside(status: DjiLinkStatus): boolean {
  return status === 'dismissed' || status === 'invalid';
}

/** The query the Hub's list endpoint takes. Empty filters are left out. */
export function toListQuery(filters: DjiFlightFilters): Record<string, string> {
  const query: Record<string, string> = {};
  if (filters.statuses?.length) query['status'] = filters.statuses.join(',');
  if (filters.organizationId) query['organizationId'] = filters.organizationId;
  if (filters.droneId) query['droneId'] = filters.droneId;
  if (filters.from) query['from'] = filters.from;
  if (filters.to) query['to'] = filters.to;
  if (filters.page && filters.page > 1) query['page'] = String(filters.page);
  if (filters.limit) query['limit'] = String(filters.limit);
  return query;
}

/** A plain hint for a row that has nothing to link yet, worked out from the row alone (the detail page has the full reason). */
export function rowHint(row: DjiFlightRow): string | null {
  if (row.linkStatus === 'dismissed' || row.linkStatus === 'invalid') return null;
  if (!row.drone) return row.linkStatus === 'ambiguous' ? 'More than one drone has this serial' : 'No drone matched';
  if (!row.organizationId) return 'No organization at flight time';
  if (row.linkStatus === 'ambiguous') return 'Needs a person to choose';
  return null;
}

/** A moment in the admin's own time zone, with the zone named so it is never mistaken for the field's local time; '—' when unknown. */
export function formatInstant(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
}

/** '1h 05m' / '12m 30s' / '45s'; null when unknown. */
export function formatDuration(seconds: number | null | undefined): string | null {
  if (seconds == null || !isFinite(seconds) || seconds < 0) return null;
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

const MPS_TO_MPH = 2.2369362920544;

/** Average speed for display; null means DJI's detail file has not reported one yet. */
export function formatSpeed(mps: number | null | undefined): string | null {
  if (mps == null || !isFinite(mps)) return null;
  return `${(mps * MPS_TO_MPH).toFixed(1)} mph (${mps.toFixed(1)} m/s)`;
}

/** What DJI says was applied: liquid in litres, granular in kilograms. */
export function formatApplied(detail: Pick<DjiFlightDetail, 'usageType' | 'sprayVolumeMl' | 'sowVolumeG'>): string | null {
  if (detail.sprayVolumeMl != null) return `${(detail.sprayVolumeMl / 1000).toFixed(2)} L sprayed`;
  if (detail.sowVolumeG != null) return `${(detail.sowVolumeG / 1000).toFixed(2)} kg sown`;
  return null;
}

export function detailStatusLabel(status: DjiFlightRow['detailStatus']): string {
  switch (status) {
    case 'ready':
      return 'Detail file received';
    case 'processing':
      return 'Waiting for the detail file';
    default:
      return 'Detail file did not arrive';
  }
}

/** The router link for the drone a flight is matched to, or null when the admin portal has no page for it. */
export function droneRouterLink(drone: DjiFlightRow['drone']): { commands: unknown[]; queryParams?: Record<string, string> } | null {
  if (!drone) return null;
  if (drone.source === 'adn') return { commands: ['/drones', drone.id, 'details'] };
  // Organization drones have no page of their own: they live in the organization's drones tab.
  return drone.organizationId ? { commands: ['/organizations', drone.organizationId], queryParams: { tab: 'drones' } } : null;
}

/**
 * The admin page for a link's target, or null when the portal has none. A
 * spray record opens on its flight record page. A job has no page of its own,
 * but a flight only reaches a job through its spray record, so the job opens
 * that record. A drone opens as `droneRouterLink` does.
 */
export function linkRouterTarget(
  link: Pick<DjiLinkView, 'targetType' | 'targetId'>,
  flight: Pick<DjiFlightDetail, 'drone' | 'links'>,
): { commands: unknown[]; queryParams?: Record<string, string> } | null {
  switch (link.targetType) {
    case 'spray_record':
      return { commands: ['/flight-records', link.targetId] };
    case 'job': {
      const record = flight.links.find((l) => l.targetType === 'spray_record');
      return record ? { commands: ['/flight-records', record.targetId] } : null;
    }
    case 'adn_drone':
    case 'organization_drone':
      return flight.drone?.id === link.targetId ? droneRouterLink(flight.drone) : null;
    default:
      return null;
  }
}

export interface DjiActionAvailability {
  setDrone: boolean;
  unlinkDrone: boolean;
  unlinkSprayRecord: boolean;
  dismiss: boolean;
  markInvalid: boolean;
  restore: boolean;
  relink: boolean;
}

/**
 * Which actions the Hub will accept for a flight, so the screen only offers
 * those (the Hub still enforces them): a set-aside flight can only be
 * restored, and a flight linked to a spray record must have that unlinked
 * before it is dismissed or its drone is changed.
 */
export function availableActions(flight: Pick<DjiFlightRow, 'linkStatus' | 'drone' | 'sprayRecordId'>): DjiActionAvailability {
  const setAside = isSetAside(flight.linkStatus);
  const hasRecord = !!flight.sprayRecordId;
  const none = { setDrone: false, unlinkDrone: false, unlinkSprayRecord: false, dismiss: false, markInvalid: false, restore: false, relink: false };
  if (setAside) return { ...none, restore: true };
  return {
    setDrone: !hasRecord,
    unlinkDrone: !!flight.drone && !hasRecord,
    unlinkSprayRecord: hasRecord,
    dismiss: !hasRecord,
    markInvalid: !hasRecord,
    restore: false,
    relink: flight.linkStatus !== 'unverified',
  };
}

/** A plain sentence for what a linkage re-run did. */
export function relinkMessage(outcome: DjiRelinkOutcome): string {
  if (outcome.sprayRecord === 'linked') {
    return outcome.jobLinked ? 'Linked to a spray record and its job.' : 'Linked to a spray record.';
  }
  if (outcome.sprayRecord === 'already_linked') return 'Already linked to a spray record. Nothing changed.';

  const why: Record<string, string> = {
    no_overlap: 'No spray record has a logged flight that overlaps this one yet.',
    ambiguous: 'More than one spray record matches, so none was linked. Choose one yourself.',
    no_drone: droneProblem(outcome.drone),
    no_owner: ownerProblem(outcome.owner),
    no_flight_window: 'DJI did not report when this flight started and ended, so it cannot be matched.',
  };
  const detail = why[outcome.sprayRecord] ?? 'No spray record was linked.';
  const matched = outcome.drone === 'linked' || outcome.drone === 'already_linked';
  return matched ? `Matched the drone, but ${lowerFirst(detail)}` : `Not linked. ${detail}`;
}

function droneProblem(drone: string): string {
  switch (drone) {
    case 'no_match':
      return 'No drone has this serial number.';
    case 'ambiguous':
      return 'More than one drone has this serial number.';
    case 'no_identifier':
      return 'DJI sent no serial number for this aircraft.';
    default:
      return 'The flight is not matched to a drone.';
  }
}

function ownerProblem(owner: string): string {
  switch (owner) {
    case 'unassigned':
      return 'The drone was not assigned to any organization at the time of this flight.';
    case 'ambiguous':
      return 'The drone changed organization during this flight, or the organization on the flight does not match its owner.';
    case 'no_flight_window':
      return 'DJI did not report when this flight started, so its owner cannot be worked out.';
    default:
      return 'No organization owned the drone at the time of this flight.';
  }
}

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

/** The words for an action in the history. */
export function actionLabel(action: string): string {
  const labels: Record<string, string> = {
    set_drone_and_organization: 'Set the drone and organization',
    unlink_drone: 'Unlinked the drone',
    unlink_spray_record: 'Unlinked the spray record',
    dismiss: 'Dismissed',
    mark_invalid: 'Marked invalid',
    restore: 'Restored',
    relink: 'Re-ran linkage',
  };
  return labels[action] ?? action;
}
